import React, { useState, useEffect } from "react";
import { Order, OrderStatus } from "../types";
import { orderService } from "../services/dataService";
import { formatCurrency, getImageUrl } from "../utils/formatters";
import AlertModal from "./AlertModal";
import "./OrderDetailModal.css";

interface OrderDetailModalProps {
  isOpen: boolean;
  orderId: string;
  onClose: () => void;
}

interface OrderLog {
  logId: string;
  orderId: string;
  changedAt: string;
  changeType: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  changedByUserName: string;
  description?: string;
  changeToken?: string;
}

interface GroupedChange {
  changeToken: string;
  changedAt: string;
  changedByUserName: string;
  changes: OrderLog[];
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  isOpen,
  orderId,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<Order | null>(null);
  const [groupedChanges, setGroupedChanges] = useState<GroupedChange[]>([]);
  const [activeTab, setActiveTab] = useState<"info" | "history">("info");
  const [imagePreviewModal, setImagePreviewModal] = useState<{
    show: boolean;
    imageUrl: string;
  }>({ show: false, imageUrl: "" });

  // Alert Modal
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  useEffect(() => {
    if (isOpen && orderId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId]);

  const groupLogsByChangeToken = (logs: OrderLog[]): GroupedChange[] => {
    const grouped: { [key: string]: GroupedChange } = {};

    logs.forEach((log) => {
      const token = log.changeToken || log.logId;
      if (!grouped[token]) {
        grouped[token] = {
          changeToken: token,
          changedAt: log.changedAt,
          changedByUserName: log.changedByUserName,
          changes: [],
        };
      }
      grouped[token].changes.push(log);
    });

    return Object.values(grouped).sort(
      (a, b) =>
        new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime(),
    );
  };

  const loadData = async () => {
    try {
      setLoading(true);

      const [orderData, logsData] = await Promise.all([
        orderService.getById(orderId),
        orderService.getOrderLogs(orderId),
      ]);

      if (!orderData) {
        setAlertModal({
          isOpen: true,
          message: "Sipariş bulunamadı!",
          type: "error",
        });
        return;
      }

      setOrder(orderData);
      const sortedLogs = logsData.sort(
        (a, b) =>
          new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime(),
      );
      setGroupedChanges(groupLogsByChangeToken(sortedLogs));
    } catch (error) {
      console.error("❌ Failed to load order:", error);
      setAlertModal({
        isOpen: true,
        message: "Sipariş yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getStatusColor = (status?: OrderStatus) => {
    if (status === undefined) return "#95a5a6";
    const colors: { [key: number]: string } = {
      [OrderStatus.Atanmadi]: "#f39c12",
      [OrderStatus.Islemde]: "#3498db",
      [OrderStatus.Tamamlandi]: "#27ae60",
      [OrderStatus.IptalEdildi]: "#e74c3c",
    };
    return colors[status] || "#95a5a6";
  };

  const getStatusLabel = (status?: OrderStatus) => {
    if (status === undefined) return "Bilinmiyor";
    const labels: { [key: number]: string } = {
      [OrderStatus.Atanmadi]: "Atanmadı",
      [OrderStatus.Islemde]: "İşlemde",
      [OrderStatus.Tamamlandi]: "Tamamlandı",
      [OrderStatus.IptalEdildi]: "İptal Edildi",
    };
    return labels[status] || "Bilinmiyor";
  };

  const getChangeIcon = (changeType: string, fieldName?: string) => {
    const icons: { [key: string]: string } = {
      Status: "📊",
      StatusChanged: "📊",
      Workshop: "🏭",
      WorkshopChanged: "🏭",
      Operator: "👷",
      OperatorChanged: "👷",
      AssignedUser: "👷",
      Price: "💰",
      PriceChanged: "💰",
      PriceCurrency: "💱",
      Quantity: "📦",
      QuantityChanged: "📦",
      Priority: "⚡",
      PriorityChanged: "⚡",
      Deadline: "📅",
      DeadlineChanged: "📅",
      Note: "📝",
      NoteChanged: "📝",
      Model: "👗",
      ModelChanged: "👗",
      Firm: "🏢",
      FirmChanged: "🏢",
      Modelist: "✂️",
      Images: "🖼️",
      Unit: "📏",
      Technics: "🧵",
      Created: "✨",
      Updated: "🔄",
    };
    if (changeType === "Other" && fieldName && icons[fieldName]) {
      return icons[fieldName];
    }
    return icons[changeType] || "📌";
  };

  const getChangeTypeLabel = (changeType: string, fieldName?: string) => {
    const labels: { [key: string]: string } = {
      Status: "Statü",
      StatusChanged: "Statü",
      Workshop: "Atölye",
      WorkshopChanged: "Atölye",
      Operator: "Operatör",
      OperatorChanged: "Operatör",
      AssignedUser: "Operatör",
      Price: "Fiyat",
      PriceChanged: "Fiyat",
      PriceCurrency: "Para Birimi",
      Quantity: "Miktar",
      QuantityChanged: "Miktar",
      Priority: "Öncelik",
      PriorityChanged: "Öncelik",
      Deadline: "Son Tarih",
      DeadlineChanged: "Son Tarih",
      Note: "Not",
      NoteChanged: "Not",
      Model: "Model",
      ModelChanged: "Model",
      Firm: "Firma",
      FirmChanged: "Firma",
      Modelist: "Modelist",
      Images: "Görseller",
      Unit: "Birim",
      Technics: "Teknikler",
      Created: "Oluşturuldu",
      Updated: "Güncellendi",
    };
    if (changeType === "Other" && fieldName && labels[fieldName]) {
      return labels[fieldName];
    }
    return labels[changeType] || changeType;
  };

  if (!isOpen) return null;

  return (
    <div className="order-detail-modal-overlay" onClick={onClose}>
      <div className="order-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="order-detail-modal__header">
          <h2>Sipariş Detayı</h2>
          <button className="order-detail-modal__close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="order-detail-modal__content">
          {loading ? (
            <div className="order-detail-modal__loading">
              <div className="spinner"></div>
              <p>Yükleniyor...</p>
            </div>
          ) : order ? (
            <>
              {/* Tabs */}
              <div className="order-detail-modal__tabs">
                <button
                  className={`tab ${activeTab === "info" ? "active" : ""}`}
                  onClick={() => setActiveTab("info")}
                >
                  📋 Bilgiler
                </button>
                <button
                  className={`tab ${activeTab === "history" ? "active" : ""}`}
                  onClick={() => setActiveTab("history")}
                >
                  📜 Geçmiş
                </button>
              </div>

              {/* Tab Content */}
              <div className="order-detail-modal__tab-content">
                {activeTab === "info" && (
                  <div className="info-tab">
                    {/* Status Badge */}
                    <div
                      className="status-banner"
                      style={{ backgroundColor: getStatusColor(order.status) }}
                    >
                      {getStatusLabel(order.status)}
                    </div>

                    {/* Info Grid */}
                    <div className="info-grid">
                      <div className="info-card">
                        <h4>🏢 Firma & Model</h4>
                        <div className="info-item">
                          <span className="label">Firma:</span>
                          <span className="value">
                            {order.firm?.firmName || "-"}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="label">Model:</span>
                          <span className="value">
                            {order.model?.modelCode ||
                              order.model?.modelName ||
                              "-"}
                          </span>
                        </div>
                      </div>

                      <div className="info-card">
                        <h4>📦 Miktar & Fiyat</h4>
                        <div className="info-item">
                          <span className="label">Miktar:</span>
                          <span className="value">{order.quantity} adet</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Birim Fiyat:</span>
                          <span className="value">
                            {order.price
                              ? formatCurrency(
                                  order.price,
                                  order.priceCurrency ||
                                    order.currency ||
                                    "TRY",
                                )
                              : "-"}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="label">Toplam:</span>
                          <span className="value total">
                            {order.price
                              ? formatCurrency(
                                  order.price * order.quantity,
                                  order.priceCurrency ||
                                    order.currency ||
                                    "TRY",
                                )
                              : "-"}
                          </span>
                        </div>
                      </div>

                      <div className="info-card">
                        <h4>🏭 Atölye & Ekip</h4>
                        <div className="info-item">
                          <span className="label">Atölye:</span>
                          <span className="value">
                            {order.workshop?.name || "Atanmadı"}
                          </span>
                        </div>

                        <div className="info-item">
                          <span className="label">Desinatör:</span>
                          <span className="value">
                            {order.modelistUser
                              ? `${order.modelistUser.firstName} ${order.modelistUser.lastName}`
                              : "Atanmadı"}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="label">Öncelik:</span>
                          <span className="value">
                            {order.priority || "Normal"}
                          </span>
                        </div>
                      </div>

                      <div className="info-card">
                        <h4>📅 Tarihler</h4>
                        <div className="info-item">
                          <span className="label">Kabul:</span>
                          <span className="value">
                            {formatDate(order.acceptanceDate)}
                          </span>
                        </div>
                        {order.deadline && (
                          <div className="info-item">
                            <span className="label">Termin:</span>
                            <span className="value">
                              {formatDate(order.deadline)}
                            </span>
                          </div>
                        )}
                        {order.completionDate && (
                          <div className="info-item">
                            <span className="label">Tamamlanma:</span>
                            <span className="value">
                              {formatDate(order.completionDate)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Teknikler */}
                    {order.orderTechnics && order.orderTechnics.length > 0 && (
                      <div className="technics-section">
                        <h4>🔧 Teknikler</h4>
                        <div className="technic-tags">
                          {order.orderTechnics.map((ot) => (
                            <span
                              key={ot.orderTechnicId}
                              className="technic-tag"
                            >
                              {ot.technic?.name || "-"}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Not */}
                    {order.note && (
                      <div className="note-section">
                        <h4>📝 Not</h4>
                        <p>{order.note}</p>
                      </div>
                    )}

                    {/* Görseller */}
                    {order.images && order.images.length > 0 && (
                      <div className="images-section">
                        <h4>🖼️ Görseller</h4>
                        <div className="images-grid">
                          {order.images.map((img) => (
                            <img
                              key={img.orderImageId}
                              src={getImageUrl(img.imageUrl)}
                              alt={img.description || "Sipariş"}
                              onClick={() =>
                                setImagePreviewModal({
                                  show: true,
                                  imageUrl: getImageUrl(img.imageUrl),
                                })
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "history" && (
                  <div className="history-tab">
                    {groupedChanges.length === 0 ? (
                      <div className="no-history">
                        <p>Henüz değişiklik kaydı bulunmuyor.</p>
                      </div>
                    ) : (
                      <div className="timeline">
                        {groupedChanges.map((group) => (
                          <div
                            key={group.changeToken}
                            className="timeline-item"
                          >
                            <div className="timeline-dot"></div>
                            <div className="timeline-content">
                              <div className="timeline-header">
                                <span className="time">
                                  {formatDate(group.changedAt)}
                                </span>
                                <span className="user">
                                  {group.changedByUserName}
                                </span>
                              </div>
                              <div className="timeline-changes">
                                {group.changes.map((change, idx) => (
                                  <div key={idx} className="change-item">
                                    <span className="change-icon">
                                      {getChangeIcon(
                                        change.changeType,
                                        change.fieldName,
                                      )}
                                    </span>
                                    <span className="change-type">
                                      {getChangeTypeLabel(
                                        change.changeType,
                                        change.fieldName,
                                      )}
                                    </span>
                                    {change.oldValue && change.newValue && (
                                      <span className="change-values">
                                        <span className="old">
                                          {change.oldValue}
                                        </span>
                                        <span className="arrow">→</span>
                                        <span className="new">
                                          {change.newValue}
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="order-detail-modal__error">
              <p>Sipariş bulunamadı</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="order-detail-modal__footer">
          <button
            className="order-detail-modal__btn order-detail-modal__btn--close"
            onClick={onClose}
          >
            Kapat
          </button>
        </div>
      </div>

      {/* Image Preview Modal */}
      {imagePreviewModal.show && (
        <div
          className="image-preview-overlay"
          onClick={() => setImagePreviewModal({ show: false, imageUrl: "" })}
        >
          <img src={imagePreviewModal.imageUrl} alt="Preview" />
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default OrderDetailModal;
